"use client";

import React, { useEffect, useRef, useImperativeHandle, forwardRef } from "react";
import { AgCharts, type AgChartOptions } from "ag-charts-community";

export type AgChartRendererHandle = {
  getImageDataURL: () => string | null;
};

type Props = {
  options: AgChartOptions;
  className?: string;
};

const AgChartRenderer = forwardRef<AgChartRendererHandle, Props>(({ options, className }, ref) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<any>(null);

  useImperativeHandle(ref, () => ({
    getImageDataURL: () => {
      const canvas = containerRef.current?.querySelector("canvas") as HTMLCanvasElement | null;
      return canvas ? canvas.toDataURL("image/png") : null;
    },
  }));

  // Create chart once and destroy on unmount
  useEffect(() => {
    if (!containerRef.current) return;

    chartRef.current = AgCharts.create({
      ...options,
      container: containerRef.current,
    });

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, []);

  // Update chart when options change
  useEffect(() => {
    if (!containerRef.current || !chartRef.current) return;

    chartRef.current.update({
      ...options,
      container: containerRef.current,
    });
  }, [options]);

  return <div ref={containerRef} className={className ?? "h-full w-full"} />;
});

export default AgChartRenderer;